import { lazy } from "react";

import { Roles } from "../constants";

// PROPOSED — not wired into any .env file yet. See the matching IS_PRODUCTION
// constant and comment in components/Common/Sidebar.tsx: this mirrors it so
// the route guard here doesn't drift out of sync with what the sidebar link
// shows. Currently always false (REACT_APP_ENV isn't set anywhere yet), so
// behaviour below is unchanged until that var is added to the prod .env.
const IS_PRODUCTION = process.env.REACT_APP_ENV === "production";

const Home = lazy(() => import("../pages/Home"));

const Login = lazy(() => import("../pages/auth/LoginPage"));

const AcceptInvite = lazy(() => import("../pages/admin/AcceptInvite"));
const InviteAdmin = lazy(() => import("../pages/admin/InviteAdmin"));
const Guardrails = lazy(() => import("../pages/dimeai/Guardrails"));
const DimeAiTestBench = lazy(() => import("../pages/dimeai/DimeAiTestBench"));
const DimeAiReferenceData = lazy(
  () => import("../pages/dimeai/ReferenceData"),
);
const TokenUsage = lazy(() => import("../pages/dimeai/TokenUsage"));
const DimeAiFeedback = lazy(() => import("../pages/dimeai/DimeAiFeedback"));
const TagIndependentMode = lazy(
  () => import("../pages/dimeai/TagIndependentMode"),
);
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));
const ListRecords = lazy(() => import("../pages/records/ListRecords"));
const UploadGL = lazy(() => import("../pages/records/UploadGL"));
const DefaultDashboard = lazy(
  () => import("../pages/dimerec/DefaultDashboard"),
);
const Dashboard = lazy(() => import("../pages/dimerec/Dashboard"));
const Company = lazy(() => import("../pages/company/Company"));
const CompanyDetails = lazy(() => import("../pages/company/CompanyDetails"));
const PrepareReports = lazy(() => import("../pages/reports/PrepareReports"));
const CreateReport = lazy(() => import("../pages/xplain/CreateReport"));
const DimeAI = lazy(() => import("../pages/dimeai/DimeAI"));
const PrivacyPolicy = lazy(() => import("../pages/auth/PrivacyPolicyPage"));
const Eula = lazy(() => import("../pages/auth/EulaPage"));
const AcceptTerms = lazy(() => import("../pages/auth/AcceptTermsPage"));
const ManageCoa = lazy(() => import("../pages/sheet/ManageCoaPage"));
const MachineLearning = lazy(() => import("../pages/admin/MachineLearningPage"));
const AuditTrail = lazy(() => import("../pages/admin/AuditTrailPage"));
const PrivacyRequests = lazy(() => import("../pages/admin/PrivacyRequestsPage"));
const SupportPage = lazy(() => import("../pages/SupportPage"));
const ContactPrivacyTeam = lazy(
  () => import("../pages/trustCenter/ContactPrivacyTeamPage"),
);
const ManageYourData = lazy(
  () => import("../pages/trustCenter/ManageYourDataPage"),
);
const AiAlgorithmTransparency = lazy(
  () => import("../pages/trustCenter/AiAlgorithmTransparencyPage"),
);
const SubProcessors = lazy(
  () => import("../pages/trustCenter/SubProcessorsPage"),
);

export const routes = [
  {
    path: "/select-service/",
    component: Home,
    protected: false,
    withMainLayout: true,
    rolesRequired: [
      Roles.GLOBAL_ADMIN,
      Roles.CHECKER,
      Roles.MAKER,
      Roles.REGIONAL_ADMIN,
    ],
  },
  {
    path: "/login/",
    component: Login,
    protected: false,
  },
  {
    path: "/forgot-password/",
    component: ForgotPassword,
    protected: false,
  },
  {
    path: "/reset-password/",
    component: ResetPassword,
    protected: false,
  },
  {
    path: "/invite/",
    component: InviteAdmin,
    protected: true,
    rolesRequired: [Roles.GLOBAL_ADMIN, Roles.REGIONAL_ADMIN],
    withMainLayout: true,
  },
  {
    path: "/admin/setup-account/",
    component: AcceptInvite,
    protected: false,
    rolesRequired: [Roles.GLOBAL_ADMIN],
    withMainLayout: true,
  },
  {
    path: "/records/",
    component: ListRecords,
    protected: true,
    rolesRequired: [
      Roles.GLOBAL_ADMIN,
      Roles.CHECKER,
      Roles.MAKER,
      Roles.REGIONAL_ADMIN,
      // View-only DimeRec/DimeXplain tag — without this, a user carrying
      // only RECORDS_VIEWER (no other role here) gets bounced straight
      // back to the dashboard before the homepage list even loads.
      Roles.RECORDS_VIEWER,
    ],
    withMainLayout: true,
  },
  {
    path: "/upload-gl/",
    component: UploadGL,
    protected: true,
    rolesRequired: [Roles.GLOBAL_ADMIN, Roles.REGIONAL_ADMIN],
    withMainLayout: true,
  },
  {
    path: "/dashboard/",
    component: DefaultDashboard,
    protected: true,
    withMainLayout: false,
  },
  {
    path: "/:role/",
    component: Dashboard,
    protected: true,
    withMainLayout: true,
  },
  {
    path: "/companies/",
    component: Company,
    protected: true,
    withMainLayout: true,
    rolesRequired: [
      Roles.GLOBAL_ADMIN,
      Roles.CHECKER,
      Roles.MAKER,
      Roles.REGIONAL_ADMIN,
    ],
  },
  {
    path: "/companies/:companyCode/",
    component: CompanyDetails,
    protected: true,
    withMainLayout: true,
    rolesRequired: [
      Roles.GLOBAL_ADMIN,
      Roles.CHECKER,
      Roles.MAKER,
      Roles.REGIONAL_ADMIN,
    ],
  },
  {
    path: "/records/:recordId/:action/",
    component: PrepareReports,
    protected: true,
    withMainLayout: true,
    // Records Viewer added so the ListOfRecords.tsx "View" button (shown to
    // anyone who isn't the assigned Maker/Checker but carries this tag)
    // doesn't get bounced back to the dashboard by this route guard before
    // Main.tsx/CheckerReports ever gets a chance to render read-only.
    rolesRequired: [Roles.MAKER, Roles.CHECKER, Roles.RECORDS_VIEWER],
  },
  {
    path: "/dime-xplain/",
    component: CreateReport,
    protected: true,
    withMainLayout: true,
    rolesRequired: [
      Roles.MAKER,
      Roles.CHECKER,
      Roles.GLOBAL_ADMIN,
      Roles.REGIONAL_ADMIN,
      Roles.RECORDS_VIEWER,
    ],
  },
  {
    path: "/dime-ai/usage/",
    component: TokenUsage,
    protected: true,
    // Reporting, not control — so a wider audience than the guardrails, which
    // decide what the assistant will answer for everyone.
    //
    // DimeAI Usage/Guardrails restricted to System Admin in production only
    // — staging intentionally shows this to all roles for testing purposes.
    // This is by design, not a bug (see 2026-09-26). See IS_PRODUCTION above
    // and the matching comment in components/Common/Sidebar.tsx.
    rolesRequired: IS_PRODUCTION
      ? [Roles.SYSTEM_ADMIN]
      : [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN, Roles.REGIONAL_ADMIN],
    withMainLayout: true,
  },
  {
    // System Admin only: these rules decide what DimeAI answers for everyone.
    path: "/dime-ai/guardrails/",
    component: Guardrails,
    protected: true,
    // TEMPORARY: Global Admin added so this can be exercised before a System
    // Admin account is available. US 1.8 says guardrails are "managed by
    // Repodime through a system admin panel", and they apply to every user in
    // every organisation — so revert to SYSTEM_ADMIN alone once tested,
    // unless the client asks otherwise.
    //
    // DimeAI Usage/Guardrails restricted to System Admin in production only
    // — staging intentionally shows this to all roles for testing purposes.
    // This is by design, not a bug (see 2026-09-26). See IS_PRODUCTION above
    // and the matching comment in components/Common/Sidebar.tsx. Note this
    // stacks with the TEMPORARY Global Admin note above: in production,
    // System Admin alone applies regardless of that note's own timeline.
    rolesRequired: IS_PRODUCTION
      ? [Roles.SYSTEM_ADMIN]
      : [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN],
    withMainLayout: true,
  },
  {
    // System Admin only, in every environment — unlike Usage/Guardrails
    // above, this surfaces real user questions and real answers, not just
    // aggregate stats or configuration, so it does not get the staging-only
    // wider-audience exception those two have.
    path: "/dime-ai/feedback/",
    component: DimeAiFeedback,
    protected: true,
    rolesRequired: [Roles.SYSTEM_ADMIN],
    withMainLayout: true,
  },
  {
    // System Admin only, in every environment: runs real questions against
    // real organisations' books, so no staging-only wider audience.
    path: "/dime-ai/test-bench/",
    component: DimeAiTestBench,
    protected: true,
    rolesRequired: [Roles.SYSTEM_ADMIN],
    withMainLayout: true,
  },
  {
    // System Admin only, in every environment — same reasoning as Feedback
    // above: these tables steer what DimeAI treats as equivalent/distinct
    // terminology for every user, not just aggregate stats.
    path: "/dime-ai/reference-data/",
    component: DimeAiReferenceData,
    protected: true,
    rolesRequired: [Roles.SYSTEM_ADMIN],
    withMainLayout: true,
  },
  {
    // System Admin only, in every environment — this decides which
    // companies/periods DimeAI answers without Tag filtering applied, a
    // data-exposure control same tier as Reference Data/Feedback above,
    // not given the staging-only wider-audience exception Usage/Guardrails
    // have.
    path: "/dime-ai/tag-independent-mode/",
    component: TagIndependentMode,
    protected: true,
    rolesRequired: [Roles.SYSTEM_ADMIN],
    withMainLayout: true,
  },
  {
    path: "/dime-ai/",
    component: DimeAI,
    protected: true,
    withMainLayout: true,
    rolesRequired: [
      Roles.MAKER,
      Roles.CHECKER,
      Roles.GLOBAL_ADMIN,
      Roles.REGIONAL_ADMIN,
    ],
  },
  {
    // Case (a): reached from AcceptTermsPage during onboarding, before terms
    // are accepted -- token exists but no service/company context yet, so
    // this must stay reachable without the /:currentService prefix.
    // protected + isGlobal keeps the URL unprefixed. PrivateRoute's own
    // isTermsPage carve-out (see PrivateRoute.tsx) stops this from being
    // redirect-looped back onto itself pre-acceptance.
    path: "/privacy-policy/",
    component: PrivacyPolicy,
    protected: true,
    isGlobal: true,
    withMainLayout: true,
  },
  {
    // Case (b): the Trust Center sidebar link, clicked from inside the app
    // with a service already selected. This is a SEPARATE route (not just
    // the one above) because Sidebar.tsx builds every other item's link as
    // `/${currentService}${item.link}` using useParams() at render time --
    // if this page were reached via the isGlobal route above, the URL would
    // have no :currentService segment, so useParams().currentService would
    // read as undefined *while sitting on this page*, and clicking any
    // other sidebar item would build a broken `/undefined/...` link. Keeping
    // the service segment in the URL here (isGlobal left false, so it gets
    // the normal /:currentService prefix from AppRoutes.tsx) keeps every
    // other sidebar link working correctly while this page is open.
    path: "/privacy-policy/",
    component: PrivacyPolicy,
    protected: true,
    withMainLayout: true,
  },
  {
    // Remaining Trust Center pages -- same pattern as the privacy policy
    // route above (protected, NOT isGlobal, withMainLayout) so they keep
    // the :currentService segment and don't repeat the "every other
    // sidebar link breaks" bug that isGlobal caused there.
    path: "/contact-privacy-team/",
    component: ContactPrivacyTeam,
    protected: true,
    withMainLayout: true,
  },
  {
    path: "/manage-your-data/",
    component: ManageYourData,
    protected: true,
    withMainLayout: true,
  },
  {
    path: "/ai-algorithm-transparency/",
    component: AiAlgorithmTransparency,
    protected: true,
    withMainLayout: true,
  },
  {
    path: "/sub-processors/",
    component: SubProcessors,
    protected: true,
    withMainLayout: true,
  },
  {
    path: "/eula/",
    component: Eula,
    protected: false,
    withMainLayout: false,
  },
  {
    path: "/accept-condition/",
    component: AcceptTerms,
    protected: false,
    withMainLayout: false,
  },
  {
    // Manage COA — Univer spreadsheet inside the app shell (sidebar "COA").
    path: "/coa/",
    component: ManageCoa,
    protected: true,
    withMainLayout: true,
    rolesRequired: [
      Roles.GLOBAL_ADMIN,
      Roles.REGIONAL_ADMIN,
      Roles.SYSTEM_ADMIN,
      Roles.COA_VIEWER,
      Roles.COA_MANAGER,
    ],
  },
  {
    // Machine Learning admin page — GL to ML class mapping (per company) +
    // default ML tags (global). System Admin only.
    path: "/ml-mapping/",
    component: MachineLearning,
    protected: true,
    withMainLayout: true,
    rolesRequired: [Roles.SYSTEM_ADMIN],
  },
  {
    // Audit Trail — every logged admin/write action, scoped server-side by
    // role (System Admin sees all companies; Global/Regional Admin see
    // only companies they have access to).
    path: "/audit-trail/",
    component: AuditTrail,
    protected: true,
    withMainLayout: true,
    rolesRequired: [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN, Roles.REGIONAL_ADMIN],
  },
  {
    // Privacy Requests -- System Admin fulfillment queue for every
    // DPDPA/CCPA/GDPR data-subject request filed via Manage Your Data /
    // Contact Privacy Team. System-Admin-only, unlike Audit Trail.
    path: "/privacy-requests/",
    component: PrivacyRequests,
    protected: true,
    withMainLayout: true,
    rolesRequired: [Roles.SYSTEM_ADMIN],
  },
  {
    // Support page — accessible to all authenticated users under each service.
    path: "/support/",
    component: SupportPage,
    protected: true,
    withMainLayout: true,
  },
];
